import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTeacher } from '@/context/TeacherContext';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Calendar, BookOpen, Clock, CheckCircle2, AlertCircle, XCircle, Loader2, Wand2, FileText, Image, Download, Eye, GripVertical, Save } from 'lucide-react';
import InlinePDFPreview from '@/components/InlinePDFPreview';
import { Switch } from '@/components/ui/switch';
import { useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import axios from 'axios';
import { apiUrl } from '@/api/config';
import { getAuthToken } from '@/utils/auth';
import * as chapterPlannerAPI from '@/api/chapterPlanner';

export default function ChapterTeachingPlanner() {
  const navigate = useNavigate();
  const { planId } = useParams();
  const { teacherTheme, isDark } = useTeacher();
  const { toast } = useToast();
  
  // Step tracking
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoadingPlan, setIsLoadingPlan] = useState(!!planId);
  
  // Step 1: Chapter Selection
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [selectedGradeId, setSelectedGradeId] = useState(null);
  const [selectedSubjectId, setSelectedSubjectId] = useState(null);
  const [selectedChapterId, setSelectedChapterId] = useState(null);
  const [chapterPdfUrl, setChapterPdfUrl] = useState(null);
  const [isLoadingSubjects, setIsLoadingSubjects] = useState(true);
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  
  // PDF Preview State (similar to chat page)
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfPaneWidth, setPdfPaneWidth] = useState(() => {
    try {
      const raw = localStorage.getItem('chapter_planner_pdf_pane_width');
      const n = raw == null ? NaN : Number(raw);
      return Number.isFinite(n) ? n : null;
    } catch {
      return null;
    }
  });
  const splitContainerRef = useRef(null);
  const isDraggingDividerRef = useRef(false);
  
  // Step 2: Teaching Capacity
  const [totalPeriods, setTotalPeriods] = useState(5);
  const [periodDuration, setPeriodDuration] = useState(40);
  
  // Plan and Period Content
  const [plan, setPlan] = useState(null);
  const [isGeneratingPlan, setIsGeneratingPlan] = useState(false);
  const [isSavingPlan, setIsSavingPlan] = useState(false);
  
  // Period Content
  const [activePeriodNumber, setActivePeriodNumber] = useState(1);
  const [activePeriod, setActivePeriod] = useState(null);
  const [isGeneratingContent, setIsGeneratingContent] = useState(false);
  

  // Fetch subjects and chapters
  useEffect(() => {
    const fetchSubjectsChapters = async () => {
      setIsLoadingSubjects(true);
      try {
        const token = getAuthToken('teacher');
        if (!token) return;
        
        const res = await axios.get(apiUrl('v1/teachers/ai-tools/subjects-chapters/'), {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const apiSubjects = res?.data?.subjects || [];
        setSubjects(apiSubjects);
        
        // Don't set any default selections - let user choose
      } catch (err) {
        console.error('Failed to load subjects and chapters', err);
        toast({
          title: "Error",
          description: "Failed to load subjects and chapters. Please refresh the page.",
          variant: "destructive",
        });
      } finally {
        setIsLoadingSubjects(false);
      }
    };
    
    fetchSubjectsChapters();
  }, [toast]);

  // Update chapters when subject changes
  useEffect(() => {
    if (selectedSubjectId && subjects.length > 0) {
      const subject = subjects.find(s => s.subject_id === parseInt(selectedSubjectId));
      if (subject) {
        setChapters(subject.chapters || []);
        // Don't auto-select first chapter - let user choose
        setSelectedChapterId(null);
      }
    } else {
      setChapters([]);
      setSelectedChapterId(null);
    }
  }, [selectedSubjectId, subjects]);

  // Load chapter content when chapter is selected
  useEffect(() => {
    if (selectedChapterId) {
      loadChapterContent();
    } else {
      // Reset PDF when no chapter is selected
      setChapterPdfUrl(null);
      setShowPdfPreview(false);
    }
  }, [selectedChapterId]);

  // Load active period when period number changes
  useEffect(() => {
    if (plan && activePeriodNumber) {
      loadPeriod(activePeriodNumber);
    }
  }, [plan, activePeriodNumber]);

  // Load existing plan if planId is provided
  useEffect(() => {
    if (planId) {
      loadExistingPlan();
    }
  }, [planId]);

  const loadExistingPlan = async () => {
    setIsLoadingPlan(true);
    try {
      const planData = await chapterPlannerAPI.getPlan(parseInt(planId));
      setPlan(planData);
      
      // Set form values from plan
      if (planData.grade) {
        setSelectedGradeId(planData.grade.id || planData.grade);
      }
      if (planData.subject) {
        setSelectedSubjectId(planData.subject.id || planData.subject);
      }
      if (planData.chapter) {
        setSelectedChapterId(planData.chapter.id || planData.chapter);
      }
      if (planData.total_periods) {
        setTotalPeriods(planData.total_periods);
      }
      if (planData.period_duration) {
        setPeriodDuration(planData.period_duration);
      }
      
      // Load chapter PDF if available
      if (planData.chapter) {
        const chapterId = planData.chapter.id || planData.chapter;
        const pdfUrl = apiUrl(`superadmin/chapters/${chapterId}/pdf/`);
        setChapterPdfUrl(pdfUrl);
        setShowPdfPreview(true);
      }
      
      // Jump to Step 2 (content view) if plan has periods, otherwise stay on Step 1
      if (planData.periods && planData.periods.length > 0) {
        const firstPeriod = planData.periods[0];
        setCurrentStep(2);
        setActivePeriodNumber(firstPeriod.period_number || 1);
        // Set the first period as active immediately
        setActivePeriod(firstPeriod);
      }
    } catch (err) {
      console.error('Failed to load plan', err);
      toast({
        title: "Error",
        description: "Failed to load plan. Redirecting to list...",
        variant: "destructive",
      });
      navigate('/teacher/ai-tools/chapter-planner');
    } finally {
      setIsLoadingPlan(false);
    }
  };

  // This useEffect is no longer needed since we removed Step 2 (period structure editing)

  // Divider drag handlers (similar to chat page)
  useEffect(() => {
    const onMove = (e) => {
      if (!isDraggingDividerRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      const root = splitContainerRef.current;
      if (!root) return;
      const rect = root.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const minLeft = 280;
      const minRight = 420;
      const maxLeft = Math.max(minLeft, rect.width - minRight);
      const next = Math.min(Math.max(x, minLeft), maxLeft);
      setPdfPaneWidth(next);
    };
    const onUp = (e) => {
      if (!isDraggingDividerRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      isDraggingDividerRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.style.pointerEvents = '';
    };
    document.addEventListener('mousemove', onMove, true);
    document.addEventListener('mouseup', onUp, true);
    const onMouseLeave = () => {
      if (isDraggingDividerRef.current) {
        isDraggingDividerRef.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.body.style.pointerEvents = '';
      }
    };
    document.addEventListener('mouseleave', onMouseLeave);
    return () => {
      document.removeEventListener('mousemove', onMove, true);
      document.removeEventListener('mouseup', onUp, true);
      document.removeEventListener('mouseleave', onMouseLeave);
    };
  }, []);

  // Save PDF pane width to localStorage
  useEffect(() => {
    try {
      if (typeof pdfPaneWidth === 'number') {
        localStorage.setItem('chapter_planner_pdf_pane_width', String(pdfPaneWidth));
      }
    } catch {}
  }, [pdfPaneWidth]);

  // Default split: 50/50 when preview is enabled
  useEffect(() => {
    if (!showPdfPreview) return;
    if (typeof pdfPaneWidth === 'number') return;
    const root = splitContainerRef.current;
    if (!root) return;
    const rect = root.getBoundingClientRect();
    if (!rect.width) return;
    const half = rect.width / 2;
    const clamped = Math.min(Math.max(half, 320), 900);
    setPdfPaneWidth(clamped);
  }, [showPdfPreview, pdfPaneWidth]);

  // Save PDF preview state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('chapter_planner_show_pdf_preview', String(showPdfPreview));
    } catch {}
  }, [showPdfPreview]);


  const loadChapterContent = async () => {
    if (!selectedChapterId) {
      setChapterPdfUrl(null);
      setShowPdfPreview(false);
      return;
    }
    
    setIsLoadingContent(true);
    try {
      // Get PDF URL from backend API endpoint
      const pdfUrl = apiUrl(`superadmin/chapters/${selectedChapterId}/pdf/`);
      setChapterPdfUrl(pdfUrl);
      
      // Auto-enable PDF preview when PDF URL is loaded
      setShowPdfPreview(true);
      
      // Also fetch chapter details to get grade_id
      const token = getAuthToken('teacher');
      if (token) {
        try {
          const chapterRes = await axios.get(
            apiUrl(`superadmin/chapters/${selectedChapterId}/`),
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const chapterData = chapterRes.data;
          if (chapterData.grade) {
            setSelectedGradeId(chapterData.grade);
          }
        } catch (err) {
          console.error('Failed to fetch chapter details for grade_id', err);
        }
      }
    } catch (err) {
      console.error('Failed to load chapter content', err);
      toast({
        title: "Error",
        description: "Failed to load chapter PDF. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingContent(false);
    }
  };

  const loadPeriod = async (periodNumber) => {
    if (!plan) {
      console.log('loadPeriod: No plan available');
      return;
    }
    
    console.log('loadPeriod: Loading period', periodNumber, 'from plan:', plan);
    
    // First, try to get period from plan.periods if available (faster, no API call)
    if (plan.periods && plan.periods.length > 0) {
      const existingPeriod = plan.periods.find(p => p.period_number === periodNumber);
      if (existingPeriod) {
        console.log('Loading period from plan.periods:', existingPeriod);
        setActivePeriod(existingPeriod);
        return;
      }
    }
    
    console.log('Period not found in plan.periods, fetching from API...');
    
    // If not found in plan.periods, fetch from API
    try {
      const periodData = await chapterPlannerAPI.getPeriod(plan.id, periodNumber);
      console.log('Fetched period data from API:', periodData);
      setActivePeriod(periodData);
      
      // Update plan.periods with fetched data
      if (plan.periods) {
        const updatedPeriods = plan.periods.map(p => 
          p.period_number === periodNumber ? periodData : p
        );
        setPlan({ ...plan, periods: updatedPeriods });
      }
    } catch (err) {
      console.error('Failed to load period', err);
      toast({
        title: "Error",
        description: "Failed to load period details.",
        variant: "destructive",
      });
    }
  };

  const handleGeneratePlan = async () => {
    if (!selectedSubjectId || !selectedChapterId) {
      toast({
        title: "Error",
        description: "Please select subject and chapter",
        variant: "destructive",
      });
      return;
    }
    
    // If grade_id is not set, try to fetch it from chapter
    let gradeId = selectedGradeId;
    if (!gradeId && selectedChapterId) {
      try {
        const token = getAuthToken('teacher');
        if (token) {
          const chapterRes = await axios.get(
            apiUrl(`superadmin/chapters/${selectedChapterId}/`),
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const chapterData = chapterRes.data;
          if (chapterData.grade) {
            gradeId = chapterData.grade;
            setSelectedGradeId(gradeId);
          }
        }
      } catch (err) {
        console.error('Failed to fetch grade_id from chapter', err);
      }
    }
    
    if (!gradeId) {
      toast({
        title: "Error",
        description: "Could not determine grade. Please try selecting the chapter again.",
        variant: "destructive",
      });
      return;
    }

    setIsGeneratingPlan(true);
    try {
      const planData = await chapterPlannerAPI.createPlan({
        grade: gradeId,
        subject: selectedSubjectId,
        chapter: selectedChapterId,
        total_periods: totalPeriods,
        period_duration: periodDuration
      });
      
      setPlan(planData);
      // After generating plan, go to Step 2 (content view)
      setCurrentStep(2);
      if (planData.periods && planData.periods.length > 0) {
        setActivePeriodNumber(1);
        setActivePeriod(planData.periods[0]);
      }
      
      toast({
        title: "Plan Generated",
        description: `Successfully generated ${totalPeriods} period plan`,
      });
    } catch (err) {
      console.error('Failed to generate plan', err);
      toast({
        title: "Error",
        description: err?.response?.data?.error || "Failed to generate plan. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingPlan(false);
    }
  };

  const handleGenerateContent = async (periodNumber) => {
    if (!plan) return;
    
    setIsGeneratingContent(true);
    try {
      const periodData = await chapterPlannerAPI.generatePeriodContent(plan.id, periodNumber);
      setActivePeriod(periodData);
      
      // Update plan periods
      if (plan.periods) {
        const updatedPeriods = plan.periods.map(p => 
          p.period_number === periodNumber ? periodData : p
        );
        setPlan({ ...plan, periods: updatedPeriods });
      }
      
      toast({
        title: "Content Generated",
        description: `Successfully generated content for Period ${periodNumber}`,
      });
    } catch (err) {
      console.error('Failed to generate content', err);
      toast({
        title: "Error",
        description: err?.response?.data?.error || "Failed to generate content. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingContent(false);
    }
  };

  const handleUpdatePeriod = async (periodNumber, data) => {
    if (!plan) return;
    
    try {
      const updatedPeriod = await chapterPlannerAPI.updatePeriod(plan.id, periodNumber, data);
      setActivePeriod(updatedPeriod);
      
      // Update plan periods
      if (plan.periods) {
        const updatedPeriods = plan.periods.map(p => 
          p.period_number === periodNumber ? updatedPeriod : p
        );
        setPlan({ ...plan, periods: updatedPeriods });
      }
      
      // Don't show toast for every auto-save, only for manual saves
    } catch (err) {
      console.error('Failed to update period', err);
      toast({
        title: "Error",
        description: err?.response?.data?.error || "Failed to update period.",
        variant: "destructive",
      });
    }
  };

  const handleSavePlan = async () => {
    if (!plan) return;
    
    setIsSavingPlan(true);
    try {
      // Save all period updates
      if (plan.periods) {
        const savePromises = plan.periods.map(period => {
          const periodData = {
            lesson_script: period.lesson_script,
            student_notes: period.student_notes,
            teaching_flow: period.teaching_flow,
            period_title: period.period_title,
            topics: period.topics
          };
          return chapterPlannerAPI.updatePeriod(plan.id, period.period_number, periodData);
        });
        
        await Promise.all(savePromises);
      }
      
      // Refresh plan to get latest data
      const updatedPlan = await chapterPlannerAPI.getPlan(plan.id);
      setPlan(updatedPlan);
      
      toast({
        title: "Plan Saved",
        description: "Your teaching plan has been saved successfully.",
      });
      
      // Navigate back to list view
      navigate('/teacher/ai-tools/chapter-planner');
    } catch (err) {
      console.error('Failed to save plan', err);
      toast({
        title: "Error",
        description: err?.response?.data?.error || "Failed to save plan.",
        variant: "destructive",
      });
    } finally {
      setIsSavingPlan(false);
    }
  };


  const handleUpdateStatus = async (periodNumber, status, completedTopics = []) => {
    if (!plan) return;
    
    setIsUpdatingStatus(true);
    try {
      const response = await chapterPlannerAPI.updatePeriodStatus(plan.id, periodNumber, status, completedTopics);
      
      // Update plan and periods
      setPlan(response.plan);
      if (response.period.period_number === activePeriodNumber) {
        setActivePeriod(response.period);
      }
      
      toast({
        title: "Status Updated",
        description: `Period ${periodNumber} marked as ${status}. Plan has been automatically adjusted.`,
      });
    } catch (err) {
      console.error('Failed to update status', err);
      toast({
        title: "Error",
        description: err?.response?.data?.error || "Failed to update status.",
        variant: "destructive",
      });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'partial':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'skipped':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500/10 text-green-600 border-green-500/20';
      case 'partial':
        return 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20';
      case 'skipped':
        return 'bg-red-500/10 text-red-600 border-red-500/20';
      default:
        return 'bg-gray-500/10 text-gray-600 border-gray-500/20';
    }
  };

  // Render Step 1: Chapter Selection
  const renderStep1 = () => (
    <div ref={splitContainerRef} className="flex flex-1 overflow-hidden gap-0 relative h-full">
      {/* Main Content - Centered when PDF is closed, left-aligned when PDF is open */}
      <div 
        className={cn(
          "flex flex-col overflow-hidden transition-all duration-300 ease-in-out h-full",
          showPdfPreview ? "w-[400px] flex-shrink-0" : "w-full max-w-2xl mx-auto"
        )}
      >
        <Card className={cn("h-full flex flex-col overflow-hidden", isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-white border-[#E0D4BF]")}>
          <CardHeader className="flex flex-row items-center justify-between flex-shrink-0">
            <CardTitle className={cn(isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
              Select Chapter
            </CardTitle>
            {/* PDF Toggle Button - Top right of container */}
            {selectedChapterId && (
              <div className="flex items-center gap-2">
                <span className={cn("text-xs font-semibold", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>PDF</span>
                <Switch
                  checked={showPdfPreview}
                  onCheckedChange={(checked) => {
                    setShowPdfPreview(!!checked);
                  }}
                />
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-4 flex-1 overflow-y-auto min-h-0">
            <div className="space-y-2">
              <Label className={cn(isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                Subject
              </Label>
              <Select
                value={selectedSubjectId?.toString() || ''}
                onValueChange={(value) => setSelectedSubjectId(parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select subject" />
                </SelectTrigger>
                <SelectContent>
                  {subjects.map((subject) => (
                    <SelectItem key={subject.subject_id} value={subject.subject_id.toString()}>
                      {subject.subject_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className={cn(isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                Chapter
              </Label>
              <Select
                value={selectedChapterId?.toString() || ''}
                onValueChange={(value) => setSelectedChapterId(parseInt(value))}
                disabled={!selectedSubjectId || chapters.length === 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select chapter" />
                </SelectTrigger>
                <SelectContent>
                  {chapters.map((chapter) => (
                    <SelectItem key={chapter.chapter_id} value={chapter.chapter_id.toString()}>
                      {chapter.chapter_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Period and Duration Selection - Only show when chapter is selected */}
            {selectedChapterId && (
              <>
                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
            <div className="space-y-2">
              <Label className={cn(isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                Number of Periods
              </Label>
              <Input
                type="number"
                min="1"
                max="50"
                value={totalPeriods}
                      onChange={(e) => setTotalPeriods(parseInt(e.target.value) || 5)}
                      className={cn(isDark ? "bg-[hsl(var(--card))]" : "bg-white")}
              />
            </div>
            <div className="space-y-2">
              <Label className={cn(isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                Duration per Period
              </Label>
              <Select
                value={periodDuration.toString()}
                onValueChange={(value) => setPeriodDuration(parseInt(value))}
              >
                      <SelectTrigger className={cn(isDark ? "bg-[hsl(var(--card))]" : "bg-white")}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 minutes</SelectItem>
                  <SelectItem value="40">40 minutes</SelectItem>
                  <SelectItem value="60">60 minutes</SelectItem>
                </SelectContent>
              </Select>
                  </div>
            </div>

              <Button
                onClick={handleGeneratePlan}
                  disabled={!selectedChapterId || isLoadingContent || isGeneratingPlan}
                  className="w-full"
              >
                {isGeneratingPlan ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating Plan...
                  </>
                ) : (
                    'Generate Chapter Plan'
                )}
              </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Resizable Divider */}
      {showPdfPreview && (
        <div
          className="w-2 h-full cursor-col-resize flex items-center justify-center bg-[hsl(var(--card-border))] hover:bg-[hsl(var(--primary))]/20 transition-colors z-10"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            isDraggingDividerRef.current = true;
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
            document.body.style.pointerEvents = 'none';
            e.currentTarget.style.pointerEvents = 'auto';
          }}
          title="Drag to resize"
        >
          <GripVertical className="w-4 h-4 text-muted-foreground" />
        </div>
      )}

      {/* Right Panel: Chapter PDF */}
      {showPdfPreview && (
        <div
          data-pdf-preview
          className="h-full p-3 transition-all duration-300 ease-in-out flex-1 overflow-hidden"
          style={{ minWidth: 400 }}
        >
          {isLoadingContent ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : chapterPdfUrl ? (
            <div className="h-full w-full overflow-hidden">
              <InlinePDFPreview
                pdfUrl={chapterPdfUrl}
                title={chapters.find(c => c.chapter_id === selectedChapterId)?.chapter_name || 'Chapter PDF'}
                page={1}
                onClose={() => setShowPdfPreview(false)}
              />
            </div>
          ) : (
            <div className={cn(
              "flex items-center justify-center h-full",
              isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]"
            )}>
              Select a chapter to view its PDF
            </div>
          )}
        </div>
      )}
    </div>
  );

  // Render Step 2: Period Content (all content is generated in one step)
  const renderStep2 = () => {
    if (!plan) return null;

    return (
      <div className="h-full flex flex-col overflow-hidden">
        {/* Period Selector */}
        <Card className={cn("flex-shrink-0 mb-4", isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-white border-[#E0D4BF]")}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 overflow-x-auto">
              {plan.periods && plan.periods.map((period) => (
                <Button
                  key={period.period_number}
                  variant={activePeriodNumber === period.period_number ? "default" : "outline"}
                  size="sm"
                  onClick={() => setActivePeriodNumber(period.period_number)}
                  className="flex items-center gap-2"
                >
                  {getStatusIcon(period.status)}
                  Period {period.period_number}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Period Content */}
        {activePeriod && (
          <div className="flex-1 flex flex-col overflow-hidden min-h-0">
            {/* Period Title Header */}
            {(() => {
              console.log('Rendering period content. ActivePeriod:', activePeriod);
              console.log('Topics:', activePeriod.topics);
              console.log('Lesson Script:', activePeriod.lesson_script);
              console.log('Student Notes:', activePeriod.student_notes);
              console.log('Questions:', activePeriod.questions);
              
              // Get period title from period_structure
              let periodTitle = `Period ${activePeriodNumber}`;
              if (plan.period_structure && Array.isArray(plan.period_structure)) {
                const periodData = plan.period_structure.find(p => p.period_number === activePeriodNumber);
                if (periodData && periodData.period_title) {
                  periodTitle = periodData.period_title;
                }
              }
              
              // Return null here - we'll show this in the Plan tab instead
              return null;
            })()}
            
            <Tabs defaultValue="plan" className="flex-1 flex flex-col h-full min-h-0">
              <TabsList className="flex-shrink-0 mb-4">
                <TabsTrigger value="plan">Plan</TabsTrigger>
                <TabsTrigger value="script">Script</TabsTrigger>
                <TabsTrigger value="notes">Class Notes</TabsTrigger>
                <TabsTrigger value="questions">Practice Questions</TabsTrigger>
                <TabsTrigger value="visuals">Visuals</TabsTrigger>
              </TabsList>

            <TabsContent value="plan" className="flex-1 data-[state=active]:flex data-[state=inactive]:hidden flex-col mt-4 h-full min-h-0">
              <Card className={cn("flex-1 flex flex-col h-full min-h-0", isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-white border-[#E0D4BF]")}>
                <CardContent className="p-6 flex-1 overflow-y-auto min-h-0">
                  <div className="space-y-6">
                    {/* Period Title and Intent */}
                    {(() => {
                      let periodTitle = `Period ${activePeriodNumber}`;
                      if (plan.period_structure && Array.isArray(plan.period_structure)) {
                        const periodData = plan.period_structure.find(p => p.period_number === activePeriodNumber);
                        if (periodData && periodData.period_title) {
                          periodTitle = periodData.period_title;
                        }
                      }
                      
                      return (
                        <div>
                          <h3 className={cn("text-xl font-bold mb-3", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                            {periodTitle}
                          </h3>
                          <div className="flex items-center gap-3 mb-6">
                            <Badge variant="outline" className="uppercase">
                              {activePeriod.intent || 'explain'}
                            </Badge>
                            <span className={cn("text-sm", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>
                              Period {activePeriodNumber} of {plan.total_periods} • {plan.period_duration} minutes
                            </span>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Topics & Subtopics */}
                    {activePeriod.topics && activePeriod.topics.length > 0 && (
                      <div>
                        <h4 className={cn("font-bold mb-3", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                          Topics & Subtopics
                        </h4>
                        <div className="space-y-3">
                          {activePeriod.topics.map((topicObj, idx) => (
                            <div key={idx} className={cn("p-3 rounded-lg", isDark ? "bg-[hsl(var(--background))]" : "bg-[#F8F5F0]")}>
                              <p className={cn("font-semibold mb-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                                {topicObj.topic || `Topic ${idx + 1}`}
                              </p>
                              {topicObj.subtopics && topicObj.subtopics.length > 0 && (
                                <ul className="list-disc list-inside space-y-1 ml-4">
                                  {topicObj.subtopics.map((subtopic, subIdx) => (
                                    <li key={subIdx} className={cn("text-sm", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>
                                      {subtopic}
                        </li>
                      ))}
                    </ul>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Teaching Flow */}
                    {activePeriod.teaching_flow && activePeriod.teaching_flow.length > 0 && (
                      <div>
                        <h4 className={cn("font-bold mb-3", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                          Teaching Flow (Very Simple)
                        </h4>
                        <ul className="list-disc list-inside space-y-2 ml-4">
                          {activePeriod.teaching_flow.map((flow, idx) => (
                            <li key={idx} className={cn(isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                              {flow}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Student Leaves Knowing */}
                    {(() => {
                      let studentLeavesKnowing = [];
                      if (plan.period_structure && Array.isArray(plan.period_structure)) {
                        const periodData = plan.period_structure.find(p => p.period_number === activePeriodNumber);
                        if (periodData && periodData.student_leaves_knowing) {
                          studentLeavesKnowing = periodData.student_leaves_knowing;
                        }
                      }
                      
                      if (studentLeavesKnowing.length > 0) {
                        return (
                          <div>
                            <h4 className={cn("font-bold mb-3", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                              Student leaves knowing:
                            </h4>
                            <ul className="space-y-1">
                              {studentLeavesKnowing.map((item, idx) => (
                                <li key={idx} className={cn("flex items-start gap-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                                  <span>✔</span>
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="script" className="flex-1 data-[state=active]:flex data-[state=inactive]:hidden flex-col mt-4 h-full min-h-0">
              <Card className={cn("flex-1 flex flex-col h-full min-h-0", isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-white border-[#E0D4BF]")}>
                <CardContent className="p-6 flex-1 overflow-y-auto min-h-0">
                  {activePeriod.lesson_script ? (
                    <div className="flex-1 flex flex-col gap-4 h-full">
                      <Textarea
                        value={activePeriod.lesson_script}
                        onChange={(e) => {
                          setActivePeriod({ ...activePeriod, lesson_script: e.target.value });
                        }}
                        onBlur={() => {
                          handleUpdatePeriod(activePeriodNumber, { lesson_script: activePeriod.lesson_script });
                        }}
                        className={cn("w-full font-mono text-sm resize-none", isDark ? "bg-[hsl(var(--card))]" : "bg-white")}
                        style={{ minHeight: '400px', height: 'calc(100% - 60px)' }}
                        placeholder="Lesson script will appear here..."
                      />
                      <div className="flex gap-2 pt-4 border-t flex-shrink-0">
                      <Button
                        onClick={() => handleGenerateContent(activePeriodNumber)}
                        disabled={isGeneratingContent}
                        variant="outline"
                      >
                        {isGeneratingContent ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Regenerating...
                          </>
                        ) : (
                          <>
                            <Wand2 className="w-4 h-4 mr-2" />
                            Regenerate Script
                          </>
                        )}
                      </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <p className={cn("mb-4", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>
                        No lesson script available.
                      </p>
                      <Button
                        onClick={() => handleGenerateContent(activePeriodNumber)}
                        disabled={isGeneratingContent}
                      >
                        {isGeneratingContent ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Wand2 className="w-4 h-4 mr-2" />
                            Generate Content
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="notes" className="flex-1 data-[state=active]:flex data-[state=inactive]:hidden flex-col mt-4 h-full min-h-0">
              <Card className={cn("flex-1 flex flex-col h-full min-h-0", isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-white border-[#E0D4BF]")}>
                <CardContent className="p-6 flex-1 overflow-y-auto min-h-0">
                  {activePeriod.student_notes ? (
                    <div className="space-y-4">
                      <div
                        className={cn(
                          "prose prose-sm max-w-none",
                          isDark ? "prose-invert text-[hsl(var(--foreground))]" : "text-[#2B2B2B]"
                        )}
                        dangerouslySetInnerHTML={{ __html: activePeriod.student_notes }}
                      />
                      <div className="flex gap-2 pt-4 border-t">
                        <Button variant="outline" size="sm">
                          <Download className="w-4 h-4 mr-2" />
                          Download
                        </Button>
                        <Button variant="outline" size="sm">
                          Share with Students
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <p className={cn("mb-4", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>
                        No student notes available.
                      </p>
                      <Button
                        onClick={() => handleGenerateContent(activePeriodNumber)}
                        disabled={isGeneratingContent}
                      >
                        Generate Content
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="visuals" className="flex-1 data-[state=active]:flex data-[state=inactive]:hidden flex-col mt-4 h-full min-h-0">
              <Card className={cn("flex-1 flex flex-col h-full min-h-0", isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-white border-[#E0D4BF]")}>
                <CardContent className="p-6 flex-1 overflow-y-auto min-h-0">
                  {activePeriod.visuals && activePeriod.visuals.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {activePeriod.visuals.map((visual, idx) => (
                        <div key={idx} className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden">
                          {visual.url ? (
                            <img src={visual.url} alt={visual.title || `Visual ${idx + 1}`} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Image className="w-8 h-8 text-gray-400" />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className={cn("text-center py-12", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>
                      No visuals available. Generate content to extract visuals.
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="questions" className="flex-1 data-[state=active]:flex data-[state=inactive]:hidden flex-col mt-4 h-full min-h-0">
              <Card className={cn("flex-1 flex flex-col h-full min-h-0", isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-white border-[#E0D4BF]")}>
                <CardContent className="p-6 flex-1 overflow-y-auto min-h-0">
                  {activePeriod.questions && Object.keys(activePeriod.questions).length > 0 ? (
                    <div className="space-y-6">
                      {[
                        { key: 'in_class', label: 'In Class' },
                        { key: 'if_time_permits', label: 'If Time Permits' },
                        { key: 'homework', label: 'Homework' }
                      ].map(({ key, label }) => (
                        activePeriod.questions[key] && activePeriod.questions[key].length > 0 && (
                          <div key={key}>
                            <h4 className={cn("font-bold mb-3", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                              {label}
                            </h4>
                            <div className="space-y-2">
                              {activePeriod.questions[key].map((q, idx) => (
                                <Card key={idx} className={cn(
                                  isDark ? "bg-[hsl(var(--background))] border-[hsl(var(--card-border))]" : "bg-[#F8F5F0] border-[#E0D4BF]"
                                )}>
                                  <CardContent className="p-4">
                                    <p className={cn(isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                                      {q.question_text || q}
                                    </p>
                                  </CardContent>
                                </Card>
                              ))}
                            </div>
                          </div>
                        )
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <p className={cn("mb-4", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>
                        No practice questions available.
                      </p>
                      <Button
                        onClick={() => handleGenerateContent(activePeriodNumber)}
                        disabled={isGeneratingContent}
                      >
                        Generate Content
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
          </div>
        )}

        {/* Back and Save Plan buttons - always visible at bottom */}
        <div className="flex gap-4 flex-shrink-0 mt-4 pt-4 border-t">
          <Button variant="outline" onClick={() => setCurrentStep(2)} className="flex-1">
            Back
          </Button>
          <Button
            onClick={handleSavePlan}
            disabled={isSavingPlan}
            className="flex-1"
          >
            {isSavingPlan ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Plan
              </>
            )}
          </Button>
        </div>
      </div>
    );
  };


  return (
    <div className={cn("flex flex-col h-full overflow-hidden", isDark ? "bg-gradient-to-br from-[hsl(var(--background))] via-[hsl(var(--card))] to-[hsl(var(--background))]" : "bg-gradient-to-br from-[#F8F5F0] via-[#F5F1EA] to-[#F2EBE0]")}>
      <div className="flex-shrink-0 p-6 pb-4">
        <div className="max-w-7xl mx-auto w-full space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/teacher/ai-tools/chapter-planner')}
              className={cn("rounded-xl", isDark ? "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--card-border))]/60" : "text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-[#F0EBE3]/60")}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className={cn("text-4xl font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>
                Chapter Planner
              </h1>
              <p className={cn("mt-1 text-sm font-normal", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#8B7B6B]")}>
                Generate AI-powered period-wise teaching plans
              </p>
            </div>
          </div>
        </div>
        </div>
      </div>
      
      {/* Step Content - Full height with split pane */}
      <div className="flex-1 overflow-hidden px-6 pb-6">
        <div className="h-full">
          {isLoadingPlan ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 animate-spin" />
            </div>
          ) : (
            <>
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

